export interface ParsedQuotationTerms {
  ourRef?: string
  yourRef?: string
  termsOfDelivery?: string
  termsOfPayment?: string
  locationOfDelivery?: string
  validity?: string
  partsOfOrigin?: string
  deliveryMeaningNote?: string
  asbestosFreeNote?: string
  overallRemarks?: string
  bankDetails?: string
  freightCost?: number
  packagingCost?: number
  truckCost?: number
  bargeCost?: number
  otherCost?: number
  vatOnLogistics?: number
  remainingNotes?: string
}

const trimValue = (value?: string | null) => {
  const normalized = String(value ?? '').trim()
  return normalized.length > 0 ? normalized : undefined
}

const splitLabelAndValue = (line: string, labelRegex: RegExp): string | undefined => {
  if (!labelRegex.test(line)) return undefined
  const value = line.replace(labelRegex, '').trim()
  return trimValue(value)
}

const parseNumericValue = (line: string, labelRegex: RegExp): number | undefined => {
  const raw = splitLabelAndValue(line, labelRegex)
  if (!raw) return undefined
  const sanitized = raw.replace(/[^0-9.\-]/g, '')
  if (!sanitized) return undefined
  const parsed = Number(sanitized)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const parseQuotationTerms = (terms?: string | null): ParsedQuotationTerms => {
  const result: ParsedQuotationTerms = {}
  const notes: string[] = []
  const source = String(terms ?? '')
  const lines = source
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  lines.forEach((line) => {
    const ourRef = splitLabelAndValue(line, /^our\s+ref\s*:\s*/i)
    if (ourRef) {
      result.ourRef = ourRef
      return
    }

    const yourRef = splitLabelAndValue(line, /^your\s+ref\s*:\s*/i)
    if (yourRef) {
      result.yourRef = yourRef
      return
    }

    const termsOfDelivery = splitLabelAndValue(line, /^terms?\s+of\s+delivery\s*:\s*/i)
    if (termsOfDelivery) {
      result.termsOfDelivery = termsOfDelivery
      return
    }

    const termsOfPayment = splitLabelAndValue(line, /^terms?\s+of\s+payment\s*:\s*/i)
    if (termsOfPayment) {
      result.termsOfPayment = termsOfPayment
      return
    }

    const locationOfDelivery = splitLabelAndValue(line, /^location\s+of\s+delivery\s*:\s*/i)
    if (locationOfDelivery) {
      result.locationOfDelivery = locationOfDelivery
      return
    }

    const validity = splitLabelAndValue(line, /^validity\s*:\s*/i)
    if (validity) {
      result.validity = validity
      return
    }

    const partsOfOrigin = splitLabelAndValue(line, /^parts?\s+of\s+origin\s*:\s*/i)
    if (partsOfOrigin) {
      result.partsOfOrigin = partsOfOrigin
      return
    }

    const deliveryMeaning = splitLabelAndValue(line, /^(d\/t\s+meaning\s+note|d\/t)\s*:\s*/i)
    if (deliveryMeaning) {
      result.deliveryMeaningNote = deliveryMeaning
      return
    }
    if (/^d\/t\s*=|delivery\s*time/i.test(line)) {
      result.deliveryMeaningNote = line
      return
    }

    const asbestosNote = splitLabelAndValue(line, /^asbestos[-\s]*free\s+note\s*:\s*/i)
    if (asbestosNote) {
      result.asbestosFreeNote = asbestosNote
      return
    }
    if (/asbestos/i.test(line)) {
      result.asbestosFreeNote = line
      return
    }

    const overallRemarks = splitLabelAndValue(line, /^overall\s+remarks\s*:\s*/i)
    if (overallRemarks) {
      result.overallRemarks = overallRemarks
      return
    }

    const remarks = splitLabelAndValue(line, /^remarks?\s*:\s*/i)
    if (remarks) {
      result.overallRemarks = remarks
      return
    }

    const bankDetails = splitLabelAndValue(line, /^bank\s*\/?\s*remittance(\s+details)?\s*:\s*/i)
    if (bankDetails) {
      result.bankDetails = bankDetails
      return
    }

    const freightCost = parseNumericValue(line, /^trucking\s*\/\s*freight\s*cost\s*:\s*/i)
    if (freightCost != null) {
      result.freightCost = freightCost
      return
    }

    const packagingCost = parseNumericValue(line, /^pkg\s*&\s*handling\s*cost\s*:\s*/i)
    if (packagingCost != null) {
      result.packagingCost = packagingCost
      return
    }

    const truckCost = parseNumericValue(line, /^truck\s*cost\s*:\s*/i)
    if (truckCost != null) {
      result.truckCost = truckCost
      return
    }

    const bargeCost = parseNumericValue(line, /^barge\s*\/\s*workboat\s*cost\s*:\s*/i)
    if (bargeCost != null) {
      result.bargeCost = bargeCost
      return
    }

    const otherCost = parseNumericValue(line, /^other\s*cost\s*:\s*/i)
    if (otherCost != null) {
      result.otherCost = otherCost
      return
    }

    const vatOnLogistics = parseNumericValue(line, /^vat\s*\/\s*gst\s*on\s*logistic\/other\s*:\s*/i)
    if (vatOnLogistics != null) {
      result.vatOnLogistics = vatOnLogistics
      return
    }

    notes.push(line)
  })

  if (notes.length > 0) {
    result.remainingNotes = notes.join('\n')
  }

  return result
}
